<template>
  <div class="shell">
    <header class="top">
      <div class="brand">星河宸镜</div>
      <button class="icon-btn" type="button" aria-label="收起菜单" @click="collapsed = !collapsed">
        <i class="el-icon-s-fold" />
      </button>
      <div class="crumb">{{ groupName }} <span>›</span> {{ pageTitle }}</div>
      <label class="search">
        <i class="el-icon-search" />
        <input v-model="keyword" placeholder="搜索订单 / 下级 / 客户" @keyup.enter="search" />
      </label>
      <button class="bell" type="button" @click="$router.push('/partner/notices')">
        <i class="el-icon-bell" />
        <em v-if="noticeCount">{{ noticeCount > 99 ? '99+' : noticeCount }}</em>
      </button>
      <div class="who">
        <button class="avatar" type="button" title="修改个人信息" @click="openProfile">{{ initial }}</button>
        <button class="who-text" type="button" title="修改个人信息" @click="openProfile">
          <b>{{ displayName }}</b>
          <small>{{ levelLabel }}</small>
        </button>
        <button class="logout" type="button" @click="logout">退出</button>
      </div>
    </header>
    <div class="body">
      <aside :class="{ collapsed }">
        <nav>
          <section v-for="group in groups" v-show="visibleItems(group).length" :key="group.name">
            <p class="group">{{ group.name }}</p>
            <router-link v-for="item in visibleItems(group)" :key="item.path" :to="item.path" class="item">
              <i :class="item.icon" />
              <span>{{ item.label }}</span>
              <em v-if="item.badge && noticeCount" class="badge">{{ noticeCount }}</em>
            </router-link>
          </section>
        </nav>
      </aside>
      <main ref="main">
        <p v-if="agreementSigned === false && levelCode !== 'ECO'" class="gate">协议扫描件还没归档，提佣先不计算。请到「协议与保证金」上传。</p>
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { getChannelNotices, getChannelOverview } from '@/api/admin'
import { levelName } from './labels'

const ECO_HIDDEN = ['/partner/lines', '/partner/statements', '/partner/withdraw', '/partner/downline', '/partner/reports', '/partner/training', '/partner/compliance', '/partner/staff']

const GROUPS = [
  {
    name: '我的经营',
    key: 'mine',
    paths: ['/partner/board', '/partner/vouchers', '/partner/recharge', '/partner/eco', '/partner/members', '/partner/works', '/partner/lines', '/partner/statements', '/partner/withdraw'],
    items: [
      { label: '我的看板', path: '/partner/board', icon: 'el-icon-data-board' },
      { label: '购买积分', path: '/partner/eco', icon: 'el-icon-coin', eco: true },
      { label: '名下会员', path: '/partner/members', icon: 'el-icon-user-solid', eco: true },
      { label: '数据资产', path: '/partner/works', icon: 'el-icon-picture-outline', eco: true },
      { label: '我的卡券', path: '/partner/vouchers', icon: 'el-icon-bank-card' },
      { label: '代金券管理', path: '/partner/recharge', icon: 'el-icon-shopping-cart-2' },
      { label: '分账明细', path: '/partner/lines', icon: 'el-icon-tickets' },
      { label: '对账单', path: '/partner/statements', icon: 'el-icon-document-checked' },
      { label: '提现管理', path: '/partner/withdraw', icon: 'el-icon-wallet' }
    ]
  },
  {
    name: '渠道拓展',
    key: 'grow',
    paths: ['/partner/downline', '/partner/reports'],
    items: [
      { label: '下级管理', path: '/partner/downline', icon: 'el-icon-share' },
      { label: '客户报备', path: '/partner/reports', icon: 'el-icon-user' }
    ]
  },
  {
    name: '成长与合规',
    key: 'comply',
    paths: ['/partner/training', '/partner/compliance'],
    items: [
      { label: '培训中心', path: '/partner/training', icon: 'el-icon-reading' },
      { label: '协议与保证金', path: '/partner/compliance', icon: 'el-icon-document' }
    ]
  },
  {
    name: '账号',
    key: 'account',
    paths: ['/partner/profile', '/partner/staff', '/partner/notices'],
    items: [
      { label: '个人信息', path: '/partner/profile', icon: 'el-icon-user' },
      { label: '子账号管理', path: '/partner/staff', icon: 'el-icon-s-custom' },
      { label: '系统通知', path: '/partner/notices', icon: 'el-icon-message', badge: true }
    ]
  }
]

export default {
  name: 'PartnerLayout',
  data() {
    return { collapsed: false, keyword: '', noticeCount: 0, levelLabel: '渠道商', levelCode: '', staffTitle: '', agreementSigned: true, groups: GROUPS }
  },
  computed: {
    ...mapGetters(['adminInfo']),
    displayName() {
      if (!this.adminInfo) return '渠道商'
      return this.adminInfo.realName || this.adminInfo.username || '渠道商'
    },
    initial() {
      return (this.displayName || '渠').slice(0, 1)
    },
    pageTitle() {
      return (this.$route.meta && this.$route.meta.menuName) || '渠道工作台'
    },
    groupName() {
      const hit = GROUPS.find(group => group.paths.indexOf(this.$route.path) >= 0)
      return hit ? hit.name : '我的经营'
    }
  },
  watch: {
    pageTitle: {
      immediate: true,
      handler(title) {
        document.title = title + ' · 星河宸镜'
      }
    },
    '$route.path'() {
      this.guardEco()
      this.$nextTick(() => {
        if (this.$refs.main) this.$refs.main.scrollTop = 0
      })
    },
    levelCode() {
      this.guardEco()
    }
  },
  created() {
    this.loadChrome()
  },
  methods: {
    ...mapActions(['adminLogout']),
    levelName,
    async loadChrome() {
      try {
        const overview = await getChannelOverview()
        const partner = (overview && overview.partner) || {}
        this.levelLabel = this.levelName(partner.levelCode)
        this.levelCode = partner.levelCode || ''
        this.staffTitle = overview.staffTitle || ''
        this.agreementSigned = overview.agreementSigned !== false
      } catch (e) {
        this.levelLabel = '渠道商'
      }
      try {
        const notices = await getChannelNotices()
        this.setNoticeCount(notices)
      } catch (e) {
        this.noticeCount = 0
      }
    },
    setNoticeCount(notices) {
      if (notices && notices.unreadCount != null) {
        this.noticeCount = notices.unreadCount
        return
      }
      const list = (notices && notices.list) || []
      this.noticeCount = list.filter(item => !item.read).length
    },
    search() {
      const keyword = this.keyword.trim()
      if (!keyword) return
      const path = this.levelCode === 'ECO' ? '/partner/members' : '/partner/downline'
      this.$router.push({ path, query: { keyword } })
    },
    guardEco() {
      if (this.levelCode === 'ECO' && ECO_HIDDEN.indexOf(this.$route.path) >= 0) {
        this.$router.replace('/partner/board')
      }
    },
    visibleItems(group) {
      const title = this.staffTitle || ''
      let allow = null
      if (title.indexOf('财务') >= 0) {
        allow = ['/partner/board', '/partner/vouchers', '/partner/recharge', '/partner/lines', '/partner/statements', '/partner/withdraw', '/partner/compliance', '/partner/notices']
      } else if (title.indexOf('培训') >= 0) {
        allow = ['/partner/board', '/partner/training', '/partner/notices']
      } else if (title.indexOf('业务') >= 0 || title.indexOf('销售') >= 0) {
        allow = ['/partner/board', '/partner/downline', '/partner/reports', '/partner/notices']
      }
      return group.items.filter(item => {
        if (item.eco && this.levelCode !== 'ECO') return false
        if (this.levelCode === 'ECO' && ECO_HIDDEN.indexOf(item.path) >= 0) return false
        if (allow && allow.indexOf(item.path) < 0) return false
        return true
      })
    },
    openProfile() {
      this.$router.push('/partner/profile')
    },
    logout() {
      this.adminLogout()
      this.$router.replace('/admin/login')
    }
  }
}
</script>

<style scoped>
.shell { height: 100vh; display: flex; flex-direction: column; background: #f3f5fb; color: #1f2a44; }
.top {
  height: 56px;
  flex: none;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 18px;
  background: #1e2a78;
  color: #fff;
}
.brand { font-size: 16px; font-weight: 700; letter-spacing: 1px; min-width: 88px; }
.icon-btn, .bell, .logout {
  border: 0;
  background: transparent;
  color: #fff;
  cursor: pointer;
  font-size: 16px;
}
.crumb { font-size: 13px; color: rgba(255,255,255,.85); white-space: nowrap; }
.crumb span { margin: 0 6px; opacity: .6; }
.search {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 280px;
  height: 34px;
  padding: 0 12px;
  border-radius: 17px;
  background: rgba(255,255,255,.12);
  color: rgba(255,255,255,.7);
}
.search input {
  flex: 1;
  border: 0;
  outline: none;
  background: transparent;
  color: #fff;
  font-size: 13px;
}
.search input::placeholder { color: rgba(255,255,255,.55); }
.bell { position: relative; width: 32px; height: 32px; }
.bell em, .badge {
  position: absolute;
  top: -2px;
  right: -4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: #f56c6c;
  color: #fff;
  font-style: normal;
  font-size: 10px;
  line-height: 16px;
}
.who { display: flex; align-items: center; gap: 8px; }
.avatar {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  background: #f0b429;
  color: #1e2a78;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.who-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  padding: 0;
  text-align: left;
}
.who-text b { font-size: 13px; font-weight: 600; color: #fff; }
.who-text small { font-size: 11px; color: rgba(255,255,255,.7); }
.logout { font-size: 12px; opacity: .85; }
.body { flex: 1; min-height: 0; display: flex; }
aside {
  width: 220px;
  flex: none;
  background: #1a2468;
  color: #d5dcf5;
  overflow: auto;
}
aside.collapsed { width: 0; overflow: hidden; }
nav { padding: 12px 0 24px; }
.group {
  margin: 14px 18px 6px;
  font-size: 12px;
  color: rgba(213,220,245,.45);
}
.item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border: 0;
  background: transparent;
  color: #d5dcf5;
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
  text-align: left;
  position: relative;
}
.item i { width: 16px; text-align: center; }
.caret { margin-left: auto; font-size: 12px; }
.item.router-link-active, .item.active {
  background: rgba(255,255,255,.08);
  color: #fff;
  box-shadow: inset 3px 0 0 #f0b429;
}
.sub { display: flex; flex-direction: column; padding: 0 0 6px 44px; }
.sub a {
  color: rgba(213,220,245,.8);
  text-decoration: none;
  font-size: 13px;
  padding: 7px 0;
}
.sub a.router-link-active { color: #fff; }
.badge { position: static; margin-left: auto; }
main { flex: 1; min-width: 0; overflow: auto; padding: 20px 24px 32px; }
.gate { margin: 0 0 12px; padding: 10px 12px; border-radius: 8px; background: #fff6e5; color: #9a6b00; font-size: 13px; }
</style>
